// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GroupListItemSchema, GroupMemberSchema, IdpGroupRegistrationSchema, PaginationQuerySchema, paginated, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
const GroupIdParamsSchema = z.object({ id: z.string().uuid() });
const GroupMemberParamsSchema = GroupIdParamsSchema.extend({ userId: z.string().uuid() });
// A group's name is delivered to deployed apps in the comma-separated
// `X-User-Groups` header (docs/41 §App access through groups), so a comma would
// split one membership into two and a control character would make the header
// unserialisable — refuse both here rather than mangle the header downstream.
// Scripts outside Latin-1 stay allowed and are reduced at the header instead, so
// an org can still name a group in its own language (docs/41 §The group model).
const GroupNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(
// eslint-disable-next-line no-control-regex
/^[^,\u0000-\u001f\u007f]+$/, 'Group names cannot contain commas or control characters: the name is delivered to apps in a comma-separated header.');
const DescriptionSchema = z.string().max(2000).nullable();
export const CreateGroupSchema = z.object({
    name: GroupNameSchema,
    description: DescriptionSchema.optional(),
});
export const UpdateGroupSchema = z
    .object({
    name: GroupNameSchema,
    description: DescriptionSchema,
})
    .partial();
export const AddGroupMemberSchema = z.object({
    user_id: z.string().uuid(),
});
// The identifier is whatever the org's IdP emits in its group claim — an Entra
// GUID, an Okta group id — so it is validated as opaque text, not as a UUID.
export const RegisterIdpGroupSchema = z.object({
    idp_group_id: z.string().trim().min(1).max(255),
    label: z.string().trim().min(1).max(255),
    description: DescriptionSchema.optional(),
});
// The identifier is immutable: a different group is a different registration.
export const UpdateIdpGroupSchema = z
    .object({
    label: z.string().trim().min(1).max(255),
    description: DescriptionSchema,
})
    .partial();
export const groupsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/groups',
        query: PaginationQuerySchema,
        responses: { 200: paginated(GroupListItemSchema), ...errorResponses },
        summary: 'List Greenlight-managed groups (cursor-paginated by name asc).',
    },
    create: {
        method: 'POST',
        path: '/api/groups',
        body: CreateGroupSchema,
        responses: { 201: GroupListItemSchema, ...errorResponses },
        summary: 'Create a Greenlight-managed group.',
    },
    update: {
        method: 'PATCH',
        path: '/api/groups/:id',
        pathParams: GroupIdParamsSchema,
        body: UpdateGroupSchema,
        responses: { 200: GroupListItemSchema, ...errorResponses },
        summary: 'Rename a group or change its description.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/groups/:id',
        pathParams: GroupIdParamsSchema,
        body: c.noBody(),
        responses: { 204: c.noBody(), ...errorResponses },
        summary: 'Delete a group and its memberships; refused while bound to an app.',
    },
    listMembers: {
        method: 'GET',
        path: '/api/groups/:id/members',
        pathParams: GroupIdParamsSchema,
        query: PaginationQuerySchema,
        responses: { 200: paginated(GroupMemberSchema), ...errorResponses },
        summary: "List a group's members (cursor-paginated by email asc).",
    },
    addMember: {
        method: 'POST',
        path: '/api/groups/:id/members',
        pathParams: GroupIdParamsSchema,
        body: AddGroupMemberSchema,
        responses: { 201: GroupMemberSchema, ...errorResponses },
        summary: 'Add a user to a group.',
    },
    removeMember: {
        method: 'DELETE',
        path: '/api/groups/:id/members/:userId',
        pathParams: GroupMemberParamsSchema,
        body: c.noBody(),
        responses: { 204: c.noBody(), ...errorResponses },
        summary: 'Remove a user from a group.',
    },
    listIdpGroups: {
        method: 'GET',
        path: '/api/idp-groups',
        query: PaginationQuerySchema,
        responses: { 200: paginated(IdpGroupRegistrationSchema), ...errorResponses },
        summary: 'List registered IdP groups (cursor-paginated by label asc).',
    },
    registerIdpGroup: {
        method: 'POST',
        path: '/api/idp-groups',
        body: RegisterIdpGroupSchema,
        responses: { 201: IdpGroupRegistrationSchema, ...errorResponses },
        summary: 'Register a directory group by identifier so owners can bind it by label.',
    },
    updateIdpGroup: {
        method: 'PATCH',
        path: '/api/idp-groups/:id',
        pathParams: GroupIdParamsSchema,
        body: UpdateIdpGroupSchema,
        responses: { 200: IdpGroupRegistrationSchema, ...errorResponses },
        summary: 'Relabel a registration or change its description.',
    },
    deregisterIdpGroup: {
        method: 'DELETE',
        path: '/api/idp-groups/:id',
        pathParams: GroupIdParamsSchema,
        body: c.noBody(),
        responses: { 204: c.noBody(), ...errorResponses },
        summary: 'Deregister an IdP group; refused while bound to an app.',
    },
});
//# sourceMappingURL=groups.js.map